import React from 'react';
import { motion } from 'framer-motion';
type Mood = 'happy' | 'thinking' | 'celebrating' | 'pointing' | 'encouraging';
interface DjayMascotProps {
  size?: number;
  mood?: Mood;
  animate?: boolean;
  className?: string;
}
/**
 * DjayMascot — faithful inline SVG van de echte Meester djay mascotte.
 * Volgt src/mascot/DjayAvatar.tsx (viewBox 200x220, default variant).
 * Vlamwezen met bril, oranje body gradient, vlam op het hoofd.
 */
export function DjayMascot({
  size = 96,
  mood = 'happy',
  animate = true,
  className = ''
}: DjayMascotProps) {
  // Eye expressions
  const renderEyes = () => {
    if (mood === 'celebrating') {
      return <g data-id="element-129">
          <path d="M60 105 Q70 95 80 105" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-130" />
          <path d="M120 105 Q130 95 140 105" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-131" />
        </g>;
    }
    if (mood === 'encouraging') {
      return <g data-id="element-132">
          <path d="M60 100 Q70 95 80 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-133" />
          <path d="M120 100 Q130 95 140 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-134" />
        </g>;
    }
    // happy / thinking / pointing — white sclera + pupil + sparkle
    const dx = mood === 'thinking' ? 3 : mood === 'pointing' ? 5 : 0;
    const dy = mood === 'thinking' ? -3 : 0;
    return <motion.g animate={animate ? {
      scaleY: [1, 1, 0.1, 1]
    } : undefined} transition={{
      duration: 4.5,
      repeat: Infinity,
      ease: 'easeInOut',
      times: [0, 0.92, 0.95, 1]
    }} style={{
      transformOrigin: '100px 102px'
    }} data-id="element-135">
        {/* Left eye */}
        <ellipse cx="70" cy="100" rx="10" ry="12" fill="white" data-id="element-136" />
        <ellipse cx={72 + dx} cy={102 + dy} rx="6" ry="7" fill="#475569" data-id="element-137" />
        <circle cx={73 + dx} cy={103 + dy} r="3.5" fill="#0F172A" data-id="element-138" />
        <circle cx={76 + dx} cy={99 + dy} r="2.5" fill="white" data-id="element-139" />
        <circle cx={69 + dx} cy={105 + dy} r="1" fill="white" fillOpacity="0.7" data-id="element-140" />

        {/* Right eye */}
        <ellipse cx="130" cy="100" rx="10" ry="12" fill="white" data-id="element-141" />
        <ellipse cx={132 + dx} cy={102 + dy} rx="6" ry="7" fill="#475569" data-id="element-142" />
        <circle cx={133 + dx} cy={103 + dy} r="3.5" fill="#0F172A" data-id="element-143" />
        <circle cx={136 + dx} cy={99 + dy} r="2.5" fill="white" data-id="element-144" />
        <circle cx={129 + dx} cy={105 + dy} r="1" fill="white" fillOpacity="0.7" data-id="element-145" />
      </motion.g>;
  };
  const renderMouth = () => {
    if (mood === 'celebrating') {
      return <g data-id="element-146">
          <path d="M80 130 Q100 165 120 130 Z" fill="#1E293B" data-id="element-147" />
          <path d="M90 145 Q100 155 110 145 Z" fill="#EF4444" data-id="element-148" />
        </g>;
    }
    if (mood === 'thinking') {
      return <path d="M90 135 Q95 132 100 135 T110 135" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-149" />;
    }
    return <path d="M82 133 Q100 152 118 133" stroke="#1E293B" strokeWidth="3.5" strokeLinecap="round" fill="none" data-id="element-150" />;
  };
  return <motion.div className={`relative inline-block ${className}`} style={{
    width: size,
    height: size * (220 / 200)
  }} animate={animate ? {
    y: [0, -3, 0],
    rotate: [0, -1, 1, 0]
  } : undefined} transition={{
    duration: 6,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-151">
      <svg viewBox="0 0 200 220" width={size} height={size * (220 / 200)} aria-label="Meester djay mascotte" data-id="element-152">
        <defs data-id="element-153">
          <linearGradient id="djay-body-grad" x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-154">
            <stop offset="0%" stopColor="#FFB057" data-id="element-155" />
            <stop offset="45%" stopColor="#FF8A00" data-id="element-156" />
            <stop offset="100%" stopColor="#E67A00" data-id="element-157" />
          </linearGradient>
          <linearGradient id="djay-flame-grad" x1="50%" y1="100%" x2="50%" y2="0%" data-id="element-158">
            <stop offset="0%" stopColor="#CC5500" data-id="element-159" />
            <stop offset="40%" stopColor="#FF8A00" data-id="element-160" />
            <stop offset="80%" stopColor="#FFD700" data-id="element-161" />
            <stop offset="100%" stopColor="#FFE066" data-id="element-162" />
          </linearGradient>
        </defs>

        {/* Shadow */}
        <ellipse cx="100" cy="205" rx="48" ry="9" fill="#1E293B" fillOpacity="0.12" data-id="element-163" />

        {/* Flame on top of head */}
        <motion.g animate={animate ? {
        scaleY: [1, 1.08, 0.95, 1.05, 1]
      } : undefined} transition={{
        duration: 2.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        transformOrigin: '100px 52px'
      }} data-id="element-164">
          <path d="M100 12 C 92 26, 86 38, 88 50 C 92 44, 96 38, 100 36 C 104 38, 108 44, 112 50 C 114 38, 108 26, 100 12 Z" fill="url(#djay-flame-grad)" data-id="element-165" />
          <path d="M100 26 C 95 36, 92 44, 96 50 C 98 46, 100 42, 100 40 C 100 42, 102 46, 104 50 C 108 44, 105 36, 100 26 Z" fill="#FFE066" opacity="0.85" data-id="element-166" />
        </motion.g>

        {/* Tail */}
        <motion.g animate={animate ? {
        rotate: [0, 6, -3, 0]
      } : undefined} transition={{
        duration: 4.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        transformOrigin: '152px 150px'
      }} data-id="element-167">
          <path d="M152 150 C 162 142, 176 140, 182 152 C 188 164, 178 178, 168 176 C 158 174, 166 160, 176 158" stroke="#E67A00" strokeWidth="10" strokeLinecap="round" fill="none" data-id="element-168" />
          <path d="M155 148 C 165 142, 175 142, 180 152" stroke="#FFB057" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-169" />
        </motion.g>

        {/* Body — rounded blob */}
        <motion.g animate={animate ? {
        rotate: [0, -1.2, 1.2, 0]
      } : undefined} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        transformOrigin: '100px 180px'
      }} data-id="element-170">
          <path d="M 35 130 C 35 75, 65 50, 100 50 C 135 50, 165 75, 165 130 C 165 170, 140 195, 100 195 C 60 195, 35 170, 35 130 Z" fill="url(#djay-body-grad)" stroke="#CC5500" strokeWidth="2" data-id="element-171" />

          {/* Belly highlight */}
          <ellipse cx="100" cy="155" rx="38" ry="28" fill="#FFD9A8" opacity="0.4" data-id="element-172" />

          {/* Arms */}
          <ellipse cx="42" cy="125" rx="18" ry="22" fill="#E67A00" transform="rotate(-20 42 125)" data-id="element-173" />
          <ellipse cx="38" cy="118" rx="8" ry="10" fill="#FFB057" transform="rotate(-20 38 118)" data-id="element-174" />
          <ellipse cx="158" cy="130" rx="18" ry="22" fill="#E67A00" transform="rotate(15 158 130)" data-id="element-175" />
          <ellipse cx="154" cy="123" rx="8" ry="10" fill="#FFB057" transform="rotate(15 154 123)" data-id="element-176" />

          {/* Feet */}
          <ellipse cx="72" cy="195" rx="18" ry="8" fill="#CC5500" data-id="element-177" />
          <ellipse cx="68" cy="192" rx="10" ry="4" fill="#FFB057" opacity="0.5" data-id="element-178" />
          <ellipse cx="128" cy="195" rx="18" ry="8" fill="#CC5500" data-id="element-179" />
          <ellipse cx="124" cy="192" rx="10" ry="4" fill="#FFB057" opacity="0.5" data-id="element-180" />

          {/* Cheeks */}
          <circle cx="48" cy="122" r="6" fill="#FCA5A5" opacity="0.55" data-id="element-181" />
          <circle cx="152" cy="122" r="6" fill="#FCA5A5" opacity="0.55" data-id="element-182" />

          {/* Eyes (with glasses-like sclera) */}
          {renderEyes()}

          {/* Glasses bridge + frames over eyes */}
          <g data-id="element-183">
            <circle cx="70" cy="100" r="13" fill="none" stroke="#1E293B" strokeWidth="2.5" opacity="0.85" data-id="element-184" />
            <circle cx="130" cy="100" r="13" fill="none" stroke="#1E293B" strokeWidth="2.5" opacity="0.85" data-id="element-185" />
            <line x1="83" y1="100" x2="117" y2="100" stroke="#1E293B" strokeWidth="2.5" opacity="0.85" data-id="element-186" />
          </g>

          {/* Mouth */}
          {renderMouth()}
        </motion.g>
      </svg>
    </motion.div>;
}
interface DjaySpeechBubbleProps {
  children: React.ReactNode;
  color?: 'orange' | 'emerald' | 'sky';
}
export function DjaySpeechBubble({
  children,
  color = 'orange'
}: DjaySpeechBubbleProps) {
  const styles = {
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900'
  }[color];
  const tail = {
    orange: 'border-orange-200 bg-orange-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    sky: 'border-sky-200 bg-sky-50'
  }[color];
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    x: -10
  }} animate={{
    opacity: 1,
    scale: 1,
    x: 0
  }} transition={{
    delay: 0.4,
    type: 'spring',
    stiffness: 300
  }} className={`relative ${styles} border-2 px-4 py-2 rounded-2xl rounded-bl-sm text-sm font-bold shadow-sm`} data-id="element-187">
      {children}
      <div className={`absolute -left-2 bottom-0 w-3 h-3 border-l-2 border-b-2 ${tail} rotate-45`} data-id="element-188" />
    </motion.div>;
}